import {
  CSSProperties,
  ChangeEventHandler,
  FC,
  PropsWithChildren,
  memo,
} from 'react'
import styled, { css } from 'styled-components'
import padStart from 'lodash/padStart'

import typeStyle from 'styles/typeStyle'

import AccommodationIcon from '../icons/Cabin'
import ExperiencesIcon from '../icons/Experiences'
import EquipmentIcon from '../icons/Equipment'
import VehicleIcon from '../icons/Kombi'
import StoriesIcon from '../icons/Stories'

import InfoIcon from '../icons/Info'
import WarningIcon from '../icons/Warning'

import Button from '../display/Button'

export const isRequired = (value: unknown) =>
  value ? undefined : 'This field is required'

export const mapSlugToIcon = {
  accommodation: AccommodationIcon,
  experiences: ExperiencesIcon,
  equipment: EquipmentIcon,
  motorVehicle: VehicleIcon,
  stories: StoriesIcon,
}

export const RadioWrapper = styled.label`
  display: flex;
  align-items: center;
  margin: 16px 0;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.carbon};

  input[type='radio'] {
    margin: 0;
    margin-right: 16px;
  }
`

export const RadioWrapperCapitalized = styled(RadioWrapper)`
  text-transform: capitalize;
`

export const Recommended = styled.span`
  font-size: 14px;
  font-weight: 400;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.carbon};
  margin-left: 8px;
`

export const TextFieldWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1;

  svg {
    position: absolute;

    top: 50%;
    width: 20px;

    height: 20px;
    right: 20px;

    color: #9b9b9b;

    transform: translateY(-50%);
  }
`

type TextProps = {
  textTransform?: string
}

type FontWeightProps = {
  fontWeight?: number
}

type HintWrapperProps = TextProps &
  FontWeightProps & {
    noLeftPadding?: boolean
  }

export const HintWrapper = styled.div<HintWrapperProps>`
  ${typeStyle('-ds-font-body-sm-regular')}
  padding-left: ${({ noLeftPadding }) => (noLeftPadding ? '0' : '30px')};
  color: ${({ theme }) => theme?.['-ds-neutral-500']?.value};
  line-height: 1.4;

  svg {
    width: 20px;
    margin-left: -30px;
    margin-right: 6px;
    margin-bottom: -4px;
    display: inline-block;
    float: left;
  }

  a {
    text-decoration: underline;
    color: ${({ theme }) => theme.brand.jungle};
  }

  text-transform: ${({ textTransform }) => textTransform || 'inherit'};

  ${({ fontWeight }) =>
    fontWeight &&
    `
    font-weight: ${fontWeight};
  `}
`

export const ListingTypeGroup = styled.div<{ homeSearch?: boolean }>`
  display: flex;

  ${({ homeSearch }) =>
    !homeSearch &&
    `
    margin: 0 -8px;
    flex-wrap: wrap;
  `}
`

export const ListingType = styled.div<{ homeSearch?: boolean }>`
  cursor: pointer;

  ${({ homeSearch }) =>
    homeSearch &&
    `
    flex: 1;
    margin-left: -1px;

    &:first-child {
      margin-left: 0;
    }
  `}

  ${({ homeSearch }) =>
    !homeSearch &&
    `
    padding: 0 8px;
    margin-bottom: 8px;
  `}
`

type HintProps = {
  className?: string
  style?: CSSProperties
  omitIcon?: boolean
}

export const Hint: FC<PropsWithChildren<HintProps>> = ({
  className = '',
  children,
  omitIcon,
  ...custom
}) => (
  <HintWrapper className={className} {...custom}>
    {!omitIcon && <InfoIcon />} {children}
  </HintWrapper>
)

const WarningWrapper = styled(HintWrapper)`
  color: ${({ theme }) => theme.colors.orange};
`

export const Warning: FC<
  PropsWithChildren<{
    className?: string
  }>
> = ({ className, children }) => (
  <WarningWrapper className={className}>
    <WarningIcon /> {children}
  </WarningWrapper>
)

type MarginProps = {
  noMargin?: boolean
  lessMargin?: boolean
  smallMargin?: boolean
}

export const InputGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => {
    const shouldForward =
      ['noMargin', 'lessMargin', 'smallMargin', 'width'].includes(prop) !== true

    return shouldForward
  },
})<MarginProps & { width?: string }>`
  position: relative;
  width: 100%;
  max-width: ${({ width }) => width || '100%'};

  ${({ noMargin }) =>
    !noMargin &&
    `
    margin-bottom: 40px;

    @media (min-width: 768px) {
      margin-bottom: ${({ lessMargin }: MarginProps) =>
        lessMargin ? '40px' : '80px'};
    }
  `}

  ${({ smallMargin }) =>
    smallMargin &&
    `
    margin-bottom: 20px;
  `}
`

export const InlineInputGroup = styled.div<{
  noWrap?: boolean
}>`
  display: flex;
  ${({ noWrap }) =>
    !noWrap &&
    `
    flex-wrap: wrap;
  `}

  margin: 0 -8px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const InputFlexWrapper = styled.div`
  width: 100%;
  display: flex;

  .inline-button {
    margin: 8px 0;
    margin-left: 15px;
  }
  input {
    flex: 1;
  }
`

export const InlineInputGroupWrapper = styled.div<{ lessMargin?: boolean }>`
  margin-bottom: 20px;
  @media (min-width: 768px) {
    margin-bottom: ${({ lessMargin }) => (lessMargin ? '20px' : '40px')};
  }
`

export const InlineInput = styled.div<{
  width?: string
  noFlex?: boolean
  $margin?: string
}>`
  flex: ${({ width, noFlex }) => (width || noFlex ? 'none' : '1')};
  width: ${({ width }) => (width ? '100%' : 'auto')};
  max-width: ${({ width }) => width || '100%'};
  margin: ${({ $margin = '0 8px' }) => $margin};
`

type LabelProps = {
  for?: string
  fontSize?: string
  forestColor?: boolean
  error?: boolean
  moreMargin?: boolean
  liteLabel?: boolean
  mediumLabel?: boolean
  bigLabel?: boolean
  checkList?: boolean
}

export const Label = styled.label<LabelProps>`
  ${typeStyle('-ds-font-lead-sm')}
  display: block;
  margin-bottom: 8px;
  ${({ fontSize }) => fontSize && `font-size: ${fontSize};`}
  color: ${({ theme, error, forestColor }) => {
    if (error) {
      return theme?.['-ds-error-300']?.value
    }
    if (forestColor) {
      return theme.brand.forest
    }

    return theme?.['-ds-neutral-700']?.value
  }};

  ${({ moreMargin }) =>
    moreMargin &&
    `
    margin-bottom: 16px;
  `}

  ${({ liteLabel }) =>
    liteLabel &&
    `
    font-weight: 300;
  `}

  ${({ mediumLabel }) =>
    mediumLabel &&
    `
    font-size: 16px;
  `}

  ${({ bigLabel }) =>
    bigLabel &&
    `
    color: black;
    font-size: 18px;
    line-height: 1.55;
    text-transform: none;
  `}

  ${({ checkList }) =>
    checkList &&
    `
    display: flex;
    align-items: baseline;
    color: #333;
    font-size: 16px;
    font-weight: 300;
    letter-spacing: 0.25px;
    text-transform: none;

    input[type='checkbox'] {
      margin-right: 8px !important;
    }
  `}
`

export const LabelMd = styled(Label)`
  font-size: 16px;
`

export const LabelLite = styled(Label)`
  ${typeStyle('-ds-font-body-light')}
`

export const RadioButtonGroup = styled.div`
  margin-bottom: 16px;
`

export const RadioLabel = styled.label`
  display: block;
  padding-top: 8px;
  font-weight: normal;
  color: #999;

  span {
    margin-left: 10px;
  }
`

export const BigLabel = styled.label<{ error?: boolean }>`
  margin-bottom: 14px;
  font-size: 20px;
  font-weight: bold;

  color: ${({ error, theme }) =>
    error ? theme?.['-ds-error-300']?.value : 'black'};
`

export const InlineLabel = styled.div`
  padding: 18px 0 18px 0;
  margin: 8px 0 8px;
  text-transform: uppercase;
  white-space: nowrap;
`

export const ErrorLabel = styled.label`
  color: ${({ theme }) => theme?.['-ds-error-300']?.value};
  font-weight: normal;
  font-size: 12px;
  margin-bottom: 5px;
`

export const InputGroupError = styled.div`
  position: relative;
  color: ${({ theme }) => theme?.['-ds-error-300']?.value};
  font-weight: normal;
  font-size: 12px;

  top: -40px;

  @media (min-width: 768px) {
    top: -80px;
  }
`

const sharedInputStyles = css<{ readOnly?: boolean; disabled?: boolean }>`
  width: 100%;
  outline: 0;
  color: ${({ theme, readOnly, disabled }) =>
    readOnly || disabled ? theme.colors.labelGrey : 'black'};
  text-overflow: ellipsis;
`

const sharedFieldStyles = css<{
  error?: boolean
  success?: boolean
  rounded?: boolean
}>`
  padding: 18px;
  margin: 8px 0 8px;
  border: 1px solid
    ${({ theme, error, success }) => {
      if (error) {
        return theme?.['-ds-error-300']?.value
      }
      if (success) {
        return theme?.['-ds-core-400']?.value
      }
      return theme.colors.textGreyLight
    }};

  &:focus,
  &:focus-within {
    border-color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  }

  ${({ rounded = true }) =>
    rounded &&
    `
    border-radius: 6px;
  `}
`

type TextFieldProps = {
  rounded?: boolean
  $white?: boolean
}

export const TextField = styled.input.attrs<TextFieldProps>(
  ({ type, rounded = true, $white }) => ({
    type: type || 'text',
    $white,
    rounded,
  }),
)<TextFieldProps>`
  ${sharedInputStyles}
  ${sharedFieldStyles}
  input {
    width: 100%;
    height: 100%;
  }

  ${({ $white, theme }) =>
    $white && `background-color: ${theme?.['-ds-neutral-100']?.value}`}
`

export const CurrencyFieldInput = styled.input`
  ${sharedInputStyles}
  border: 0;
  font-weight: 300;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const CurrencyFieldLabel = styled.label<{
  readOnly?: boolean
  disabled?: boolean
  error?: boolean
  success?: boolean
  rounded?: boolean
}>`
  display: flex;
  align-items: center;
  font-weight: 300;
  color: ${({ theme, readOnly, disabled }) =>
    readOnly || disabled ? theme.colors.labelGrey : 'black'};
  ${sharedFieldStyles}
`

export const CurrencyField = ({
  symbol = '$',
  style = undefined,
  rounded = true,
  ...props
}) => {
  const { error, success } = props
  return (
    <CurrencyFieldLabel
      error={error}
      success={success}
      style={style}
      rounded={rounded}
    >
      {symbol}
      <CurrencyFieldInput type="number" {...props} />
    </CurrencyFieldLabel>
  )
}

export const renderNumberOptions = (length = 10) =>
  [...Array(length)].map((e, i) => (
    /* eslint-disable react/no-array-index-key */
    <option key={i + 1} value={i + 1}>
      {i + 1}
    </option>
    /* eslint-enable react/no-array-index-key */
  ))

export const renderTimeOptions = () =>
  [...Array(24)].map((e, i) => {
    const timeLabel = `${i}:00`
    const timeValue = `${padStart(i.toString(), 2, '0')}:00`
    /* eslint-disable react/no-array-index-key */
    return (
      <option key={i} value={timeValue}>
        {timeLabel}
      </option>
    )
    /* eslint-enable react/no-array-index-key */
  })

export const RadioGroup = styled.div`
  label {
    font-weight: normal;
    margin-right: 16px;
    margin-bottom: 16px;

    span {
      margin-left: 12px;
    }
  }
`

export const CheckboxGroupWrapper = styled.div`
  padding-top: 4px;
  label {
    font-weight: normal;
    margin-right: 16px;
    margin-bottom: 16px;

    span {
      margin-left: 12px;
    }
  }
`

export const SelectField = styled.div.attrs<{ rounded?: boolean }>(
  ({ rounded = true }) => ({
    rounded,
  }),
)<{
  rounded?: boolean
  error?: boolean
  readOnly?: boolean
  white?: boolean
  disabled?: boolean
  isPlaceholder?: boolean
  grey?: boolean
}>`
  position: relative;
  width: 100%;
  padding: 16px;
  margin: 8px 0 8px;
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme?.['-ds-error-300']?.value : theme.colors.textGreyLight};

  background-color: ${({ white }) => (white ? 'white' : 'transparent')};

  &:focus {
    border-color: ${({ theme }) => theme.brand.jungle};
  }

  color: ${({ theme, readOnly, disabled }) =>
    readOnly || disabled ? theme.colors.labelGrey : 'black'};

  ${({ grey }) =>
    grey &&
    `
    color: #757575;
  `};

  ${({ isPlaceholder }) =>
    isPlaceholder &&
    `
    color: #757575;
  `};

  ${({ rounded = true }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  select {
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    padding: 0 18px;
    padding-right: 48px;

    appearance: none;
    overflow: hidden;
    background: transparent;
    color: currentColor;
    border: 0;
    outline: 0;
    height: 100%;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    pointer-events: none;
    top: 0;
    right: 0;

    height: 100%;
    padding: 0 18px;
  }

  svg {
    width: 13px;
    height: 6px;
  }
`

export const Submit = styled(Button)`
  ${typeStyle('-ds-font-lead')}
  display: block;
`

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  input[type='checkbox']& {
    margin-right: 8px;
  }
`

type CheckboxToggleProps = {
  id: string
  name: string
  onChange: ChangeEventHandler<HTMLInputElement>
  isChecked?: boolean
  disabled?: boolean
  style?: CSSProperties
  ['aria-label']?: string
}

export const CheckboxToggle: FC<CheckboxToggleProps> = memo(
  ({
    id,
    name,
    onChange,
    isChecked,
    disabled,
    style,
    'aria-label': ariaLabel,
  }) => (
    <div
      className="toggle-checkbox"
      style={{
        display: 'block',
        marginBottom: 12,
        marginTop: 12,
        ...style,
      }}
    >
      <input
        type="checkbox"
        id={id}
        name={name}
        onChange={onChange}
        checked={isChecked}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <span className="toggle-btn" />
    </div>
  ),
)

const OptionalTagWrapper = styled.span`
  ${typeStyle('-ds-font-body-regular')}
  color: ${({ theme }) => theme?.['-ds-neutral-500'].value};
  text-transform: capitalize;

  > span {
    margin: 0 0.5em;
  }
`

export const OptionalTag = ({ children = 'optional' }) => (
  <OptionalTagWrapper>
    <span>&ndash;</span>
    {children}
  </OptionalTagWrapper>
)
